import { useMemo, useState, type ReactNode } from "react";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import type { ProviderSetupDefinition } from "./hooks/useProviderConnections";
import { PROVIDERS, type HarperProvider } from "./provider-options";

interface ProviderConnectPanelProps {
  provider: HarperProvider;
  definitions: ProviderSetupDefinition[];
  onBack: () => void;
  backLabel?: string;
  onSaveKey: (input: {
    provider: HarperProvider;
    credential: string;
    baseUrl?: string;
    model?: string;
  }) => Promise<void>;
  onImportDesktop: (provider: HarperProvider) => Promise<void>;
  onConnected: () => void;
}

export function ProviderConnectPanel(props: ProviderConnectPanelProps) {
  const [step, setStep] = useState<"overview" | "credentials">("overview");
  const [credential, setCredential] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState<"oauth" | "key" | "import" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const definition = props.definitions.find(
    (item) => item.id === backendProviderId(props.provider),
  );
  const provider = PROVIDERS.find((item) => item.id === props.provider);
  const label = provider?.label ?? "Provider";
  const supportsDesktopOAuth =
    props.provider === "openai" &&
    (definition?.authTypes.includes("oauth") ?? false);
  const canStartDesktopOAuth = Boolean(
    window.electron?.providerAuth?.startOpenAiOAuth,
  );
  const showApiKey = props.provider !== "openai";
  const signupLabel =
    props.provider === "openai"
      ? "Codex subscription"
      : props.provider === "nous"
        ? "Create free account"
        : "Provider account";
  const oauthHeading =
    props.provider === "openai" ? "Codex sign-in" : "Desktop sign-in";
  const guidance = useMemo(
    () =>
      props.provider === "nous"
        ? "Nous Portal includes free model access. Create an account, generate a key, and paste it below."
        : (definition?.setupHint ?? `Connect ${label} to use it in this chat.`),
    [definition?.setupHint, label, props.provider],
  );

  const openSignup = () => {
    if (!definition?.signupUrl) return;
    if (window.electron?.openExternal) {
      void window.electron.openExternal(definition.signupUrl);
      return;
    }
    window.open(definition.signupUrl, "_blank", "noopener,noreferrer");
  };

  const startOAuth = async () => {
    setBusy("oauth");
    setMessage(null);
    try {
      const result = await window.electron?.providerAuth?.startOpenAiOAuth?.();
      if (!result?.ok)
        throw new Error(result?.error || "Desktop OAuth could not start.");
      setMessage(
        props.provider === "openai"
          ? "Finish your Codex sign-in in the browser, then verify the GPT 5.6 models."
          : "Finish the sign-in in your browser, then verify the connection.",
      );
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "OAuth could not start.",
      );
    } finally {
      setBusy(null);
    }
  };

  const importOAuth = async () => {
    setBusy("import");
    setMessage(null);
    try {
      await props.onImportDesktop(props.provider);
      props.onConnected();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Login is not ready yet.",
      );
    } finally {
      setBusy(null);
    }
  };

  const saveKey = async () => {
    if (credential.trim().length < 10) {
      setMessage("Paste the full provider key first.");
      return;
    }
    setBusy("key");
    setMessage(null);
    try {
      await props.onSaveKey({
        provider: props.provider,
        credential: credential.trim(),
        baseUrl: undefined,
        model: model || definition?.defaultModel,
      });
      props.onConnected();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Connection failed.");
    } finally {
      setBusy(null);
    }
  };

  if (step === "overview") {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col p-3"
        data-bloom-step="overview"
      >
        <button
          type="button"
          onClick={props.onBack}
          className="mb-3 inline-flex w-fit items-center gap-1 text-[11px] text-[var(--fintheon-muted)] hover:text-[var(--fintheon-accent)]"
        >
          <ArrowLeft size={12} /> {props.backLabel ?? "Providers"}
        </button>
        <div className="text-sm font-medium text-[var(--fintheon-text)]">
          Connect {label}
        </div>
        <p className="mt-1 text-[11px] leading-5 text-[var(--fintheon-muted)]">
          {guidance}
        </p>
        {definition?.signupUrl ? (
          <button
            type="button"
            onClick={openSignup}
            className="mt-3 inline-flex w-fit items-center gap-1.5 text-[11px] text-[var(--fintheon-accent)]"
          >
            {signupLabel} <ExternalLink size={11} />
          </button>
        ) : null}
        <div className="mt-5 flex justify-end">
          <ActionButton disabled={false} onClick={() => setStep("credentials")}>
            Continue
          </ActionButton>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col p-3"
      data-bloom-step="credentials"
    >
      <button
        type="button"
        onClick={() => setStep("overview")}
        className="mb-3 inline-flex w-fit items-center gap-1 text-[11px] text-[var(--fintheon-muted)] hover:text-[var(--fintheon-accent)]"
      >
        <ArrowLeft size={12} /> Back
      </button>
      <div className="text-sm font-medium text-[var(--fintheon-text)]">
        Connect {label}
      </div>
      <p className="mt-1 text-[11px] leading-5 text-[var(--fintheon-muted)]">
        {guidance}
      </p>

      {definition?.signupUrl ? (
        <button
          type="button"
          onClick={openSignup}
          className="mt-3 inline-flex w-fit items-center gap-1.5 text-[11px] text-[var(--fintheon-accent)]"
        >
          {signupLabel} <ExternalLink size={11} />
        </button>
      ) : null}

      {supportsDesktopOAuth ? (
        <div className="mt-4 border-t border-[color-mix(in_srgb,var(--fintheon-accent)_12%,transparent)] pt-3">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-muted)]">
            {oauthHeading}
          </div>
          <div className="mt-2 flex gap-2">
            <ActionButton
              disabled={!canStartDesktopOAuth || busy !== null}
              onClick={startOAuth}
            >
              {busy === "oauth" ? "Opening..." : "Sign in"}
            </ActionButton>
            <ActionButton
              disabled={!canStartDesktopOAuth || busy !== null}
              onClick={importOAuth}
            >
              {busy === "import"
                ? "Checking..."
                : props.provider === "openai"
                  ? "Verify GPT 5.6"
                  : "Verify connection"}
            </ActionButton>
          </div>
          {!canStartDesktopOAuth ? (
            <p className="mt-2 text-[10px] text-[var(--fintheon-muted)]">
              {props.provider === "openai"
                ? "OpenAI sign-in is available in Fintheon Desktop."
                : "OAuth setup is available in Fintheon Desktop. You can use an API key here."}
            </p>
          ) : null}
        </div>
      ) : null}

      {showApiKey ? (
        <div className="mt-4 border-t border-[color-mix(in_srgb,var(--fintheon-accent)_12%,transparent)] pt-3">
          <label className="text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-muted)]">
            API key
          </label>
          <input
            type="password"
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
            placeholder="Paste provider key"
            className="mt-2 h-9 w-full rounded-md border border-[color-mix(in_srgb,var(--fintheon-accent)_20%,transparent)] bg-[#050402] px-2.5 text-xs text-[var(--fintheon-text)] outline-none focus:border-[var(--fintheon-accent)]"
          />
          {(definition?.models.length ?? 0) > 0 ? (
            <select
              value={model || definition?.defaultModel || ""}
              onChange={(event) => setModel(event.target.value)}
              aria-label={`${label} model`}
              className="mt-2 h-9 w-full rounded-md border border-[color-mix(in_srgb,var(--fintheon-accent)_20%,transparent)] bg-[#050402] px-2.5 text-xs text-[var(--fintheon-text)] outline-none focus:border-[var(--fintheon-accent)]"
            >
              {definition?.models.map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          ) : null}
          <ActionButton disabled={busy !== null} onClick={saveKey} wide>
            {busy === "key" ? "Verifying..." : "Verify and save"}
          </ActionButton>
        </div>
      ) : null}
      {message ? (
        <p className="mt-3 text-[10px] leading-4 text-[var(--fintheon-accent)]">
          {message}
        </p>
      ) : null}
    </div>
  );
}

function ActionButton(props: {
  children: ReactNode;
  disabled: boolean;
  onClick: () => void;
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className={`${props.wide ? "mt-2 w-full" : ""} inline-flex h-9 items-center justify-center gap-1 rounded-md border border-[color-mix(in_srgb,var(--fintheon-accent)_35%,transparent)] px-3 text-[11px] text-[var(--fintheon-accent)] disabled:opacity-40`}
    >
      {props.disabled ? <Loader2 className="animate-spin" size={11} /> : null}
      {props.children}
    </button>
  );
}

function backendProviderId(provider: HarperProvider): string {
  return provider;
}
